/**
 * Seamless autoplay enhancement for slideshow components
 * Provides continuous scrolling animation instead of discrete slide transitions
 */

class SeamlessAutoplay {
  constructor(slideshow) {
    this.slideshow = slideshow;
    this.scroller = null;
    this.animationFrame = null;
    this.isPaused = false;
    this.speed = parseInt(slideshow.dataset.seamlessSpeed || '50', 10);
    this.startTime = null;
    this.startScroll = 0;
    this.isUserScrolling = false;
    this.scrollTimeout = null;
    this.retryCount = 0;
    this.maxRetries = 50; // 5 seconds max wait
    
    // Wait for slideshow to be fully initialized
    this.init();
  }

  init() {
    // Wait for the slideshow component to be connected and find the scroller
    const findScroller = () => {
      // Try to find scroller using refs if available (Horizon's pattern)
      if (this.slideshow.refs && this.slideshow.refs.scroller) {
        this.scroller = this.slideshow.refs.scroller;
      } else {
        // Fallback to querySelector
        this.scroller = this.slideshow.querySelector('slideshow-slides');
      }
      
      if (!this.scroller) {
        this.retryCount++;
        if (this.retryCount < this.maxRetries) {
          // Retry if scroller not found yet
          requestAnimationFrame(findScroller);
          return;
        } else {
          console.warn('SeamlessAutoplay: Could not find slideshow-slides element');
          return;
        }
      }
      
      // Make sure scroller has content and is scrollable
      if (this.scroller.scrollWidth <= this.scroller.clientWidth) {
        // Wait a bit more for content to load
        setTimeout(() => {
          if (this.scroller.scrollWidth > this.scroller.clientWidth) {
            this.setupScroller();
          }
        }, 500);
        return;
      }
      
      this.setupScroller();
    };
    
    findScroller();
  }

  setupScroller() {
    // Disable scroll-snap for seamless scrolling
    this.scroller.style.scrollSnapType = 'none';
    this.scroller.style.scrollBehavior = 'auto';
    
    // Track user scrolling to pause autoplay
    let lastScrollLeft = this.scroller.scrollLeft;
    this.scroller.addEventListener('scroll', () => {
      const currentScroll = this.scroller.scrollLeft;
      const scrollDelta = Math.abs(currentScroll - lastScrollLeft);
      lastScrollLeft = currentScroll;
      
      // If scroll change is significant and not from our animation, it's user scrolling
      if (scrollDelta > 1 && Math.abs(currentScroll - this.startScroll - (performance.now() - this.startTime) / 1000 * this.speed) > 5) {
        this.isUserScrolling = true;
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isUserScrolling = false;
          if (!this.isPaused) {
            this.startScroll = this.scroller.scrollLeft;
            this.startTime = performance.now();
          }
        }, 200);
      }
    }, { passive: true });
    
    // Start autoplay once scroller is ready
    setTimeout(() => {
      this.start();
    }, 200);
  }

  start() {
    if (!this.scroller || this.isPaused) return;
    
    // Make sure scroller is scrollable
    if (this.scroller.scrollWidth <= this.scroller.clientWidth) {
      console.warn('SeamlessAutoplay: Scroller is not scrollable');
      return;
    }
    
    this.startScroll = this.scroller.scrollLeft;
    this.startTime = performance.now();
    this.animate();
  }

  stop() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = null;
    }
  }

  pause() {
    this.isPaused = true;
    this.stop();
  }

  resume() {
    this.isPaused = false;
    if (this.scroller) {
      this.startScroll = this.scroller.scrollLeft;
      this.startTime = performance.now();
      this.start();
    }
  }

  animate() {
    if (this.isPaused || !this.scroller || this.isUserScrolling) {
      if (!this.isPaused && this.scroller && !this.isUserScrolling) {
        this.animationFrame = requestAnimationFrame(() => this.animate());
      }
      return;
    }

    // Check if scroller is still scrollable
    if (this.scroller.scrollWidth <= this.scroller.clientWidth) {
      return;
    }

    const now = performance.now();
    const elapsed = (now - this.startTime) / 1000; // Convert to seconds
    const scrollDistance = elapsed * this.speed; // pixels per second
    const maxScroll = this.scroller.scrollWidth - this.scroller.clientWidth;
    
    let newScroll = this.startScroll + scrollDistance;
    
    // Loop seamlessly - when we reach the end, jump back to start
    if (newScroll >= maxScroll) {
      // Reset to beginning for seamless loop
      this.scroller.scrollLeft = 0;
      this.startScroll = 0;
      this.startTime = now;
    } else {
      this.scroller.scrollLeft = newScroll;
    }

    this.animationFrame = requestAnimationFrame(() => this.animate());
  }
}

// Initialize seamless autoplay for slideshows with data-autoplay-mode="seamless"
function initSeamlessAutoplay() {
  const seamlessSlideshows = document.querySelectorAll('slideshow-component[data-autoplay-mode="seamless"]');
  
  if (seamlessSlideshows.length === 0) {
    return;
  }
  
  seamlessSlideshows.forEach((slideshow) => {
    // Skip if already initialized
    if (slideshow.__seamlessAutoplay) return;
    
    const seamless = new SeamlessAutoplay(slideshow);
    slideshow.__seamlessAutoplay = seamless;
    
    // Pause on hover if enabled
    if (slideshow.dataset.autoplayPauseOnHover === 'true') {
      slideshow.addEventListener('mouseenter', () => seamless.pause());
      slideshow.addEventListener('mouseleave', () => seamless.resume());
    }
    
    // Pause when page is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        seamless.pause();
      } else {
        seamless.resume();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });
}

// Wait for custom element to be defined, then initialize
function waitForCustomElement() {
  if (customElements.get('slideshow-component')) {
    // Custom element is defined, wait a bit for it to be ready
    setTimeout(() => {
      initSeamlessAutoplay();
    }, 100);
  } else {
    // Retry if custom element not defined yet
    setTimeout(waitForCustomElement, 100);
  }
}

// Start initialization
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForCustomElement);
} else {
  waitForCustomElement();
}

// Also handle dynamically added slideshows
const observer = new MutationObserver(() => {
  initSeamlessAutoplay();
});

if (document.body) {
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
} else {
  document.addEventListener('DOMContentLoaded', () => {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  });
}
